import { useEffect, useState, useRef } from "react";
import Table from "../customComponents/Table";
import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { post } from "../utils/api";
import ConfirmationModal from "../customComponents/ConfirmationModal";
import LoaderComponent from "./LoaderComponent";

const AssignedInstitution = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const searchText = searchParams.get("search") || "";

  const updateParams = (updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v) next.set(k, v);
        else next.delete(k);
      });
      return next;
    });
  };

  const [localSearch, setLocalSearch] = useState(searchText);
  const searchTimerRef = useRef(null);

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [deleteItem, setDeleteItem] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [noOfPages, setNoOfPages] = useState(1);

  useEffect(() => {
    fetchData();
  }, [currentPage, searchText]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const loggedUser = JSON.parse(localStorage.getItem("user"));
      const data = {
        page: currentPage,
        limit: 10,
        userid: loggedUser?.userid,
        role: loggedUser?.role,
        search: searchText.trim(),
      };

      const result = await post("/getAllAssignedInstitution", data);

      const temp = result.data.map((list) => ({
        salesperson: list.username,
        institutions: list.assigned_institutions || [],
        locations: list.assigned_locations || [],
        status: list.status,
        remarks: "-",
        ...list,
      }));

      setNoOfPages(result?.pagination?.total);
      setTableData(temp);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (data) => {
    setShowDeleteModal(true);
    setDeleteItem(data);
  };

  const handleCloseModal = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await post("/deleteAssignedInstitution", {
        userid: deleteItem.userid,
        institutionid: deleteItem.institutions.map((inst) => inst.institutionid),
      });
      toast.success("Institution deleted successfully");
      setShowDeleteModal(false);
      fetchData();
    } catch (error) {
      toast.error("Institution delete failed");
      setShowDeleteModal(false);
    }
  };

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("slNo", {
      header: "Sl. No",
      cell: (info) => info.row.index + 1,
    }),
    columnHelper.accessor("salesperson", {
      header: "SALES PERSON",
      cell: ({ row }) => (
        <div className="flex items-center">
          <span className="material-symbols-outlined w-8 h-8 mr-3 rounded-full flex items-center justify-center bg-gray-200">
            person
          </span>
          <span>{row.original.salesperson}</span>
        </div>
      ),
    }),
    columnHelper.accessor("institutions", {
      header: "INSTITUTION",
      cell: ({ row }) => {
        const institutions = row?.original?.institutions || [];
        if (institutions.length === 0) return null;
        const first = institutions[0]?.institutionname;
        const moreCount = institutions.length - 1;
        return (
          <div>
            {first}
            {moreCount > 0 && (
              <span className="text-dark-500 ml-1 bg-gray-200 p-1.5 rounded">
                +{moreCount}
              </span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("locations", {
      header: "LOCATION",
      cell: ({ row }) => {
        const locations = row?.original?.locations || [];
        if (locations.length === 0) return null;
        const first = locations[0]?.locationname;
        const moreCount = locations.length - 1;
        return (
          <div>
            {first}
            {moreCount > 0 && (
              <span className="text-dark-500 ml-1 bg-gray-200 p-1.5 rounded">
                +{moreCount}
              </span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("actions", {
      header: "ACTIONS",
      cell: (info) => (
        <div className="flex gap-2 sm:gap-1 sm:w-[200px] sm:overflow-x-auto sm:pb-2 sm:pl-2">
          <button
            onClick={() =>
              navigate("/edit-assigned-institution", {
                state: { assignInfo: info.row.original, type: "edit", isEdit: true },
              })
            }
            className="p-2 bg-gray-200 rounded-md"
          >
            <span className="material-symbols-outlined">edit</span>
          </button>
          {user?.role === "Admin" && (
            <button className="p-2 bg-gray-200 text-red-500 rounded-md">
              <span
                className="material-symbols-outlined"
                onClick={() => handleDelete(info.row.original)}
              >
                delete
              </span>
            </button>
          )}
        </div>
      ),
    }),
  ];

  return (
    <div>
      <div className="flex justify-between">
        <div>
          <h2 className="text-primary-black text-[28px] font-semibold">
            Assigned Institution
          </h2>
          <p className="text-[14px]">
            <span className="font-bold">Assigned Institution</span> /{" "}
            <span className="font-regular">Assigned Institution List</span>
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 gap-4 mt-4 md:flex md:justify-between">
        <div className="w-full md:w-5/12">
          <input
            type="text"
            className="outline-none p-2 rounded-l-lg w-full h-[40px]"
            placeholder="Search..."
            value={localSearch}
            onChange={(e) => {
              const value = e.target.value;
              setLocalSearch(value);
              clearTimeout(searchTimerRef.current);
              searchTimerRef.current = setTimeout(() => {
                updateParams({ search: value || null, page: null });
              }, 400);
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border-gray-300 mt-7 pb-7">
        {isLoading ? (
          <LoaderComponent />
        ) : tableData.length > 0 ? (
          <Table
            columns={columns}
            data={tableData}
            noOfPages={noOfPages}
            currentPage={currentPage}
            setCurrentPage={(page) => updateParams({ page: page > 1 ? String(page) : null })}
          />
        ) : (
          <div className="p-6 text-center text-gray-500">No Data Available</div>
        )}
      </div>

      {/* Delete Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        title="Delete Institution"
        message={`Are you sure you want to delete the Assigned user "${deleteItem?.username}"?`}
      />

      <ToastContainer />
    </div>
  );
};

export default AssignedInstitution;
